<script setup lang="ts">
import { ref, computed, inject, watch } from 'vue';

import { inputFocusKey, configuratorStateKey } from '../ProductConfiguratorKeys';
import useContext from '@/composables/useContext';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import ConfiguratorChevron from '../ConfiguratorChevron.vue';

const { channel, cart, getCurrencySymbol, formatPrice } = useContext();
const { configuratorState, updateActivePriceBrackets } = inject(configuratorStateKey);
const { websiteText } = useWebsiteTexts();
const { inputFocus } = inject(inputFocusKey);
const toggleState = ref(false);

if (typeof window !== 'undefined') {
  toggleState.value = JSON.parse(localStorage.getItem("configurator:bracket-accordion-open-state") || 'false');
}

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },
  hasDecoration: {
    type: Boolean,
    required: true,
  },
});

const productPriceBrackets = computed(() => {
  const locale = channel.value?.locale;
  const currency = cart.value.currency?.id;

  if (!locale || !currency) {
    return [];
  }

  
  const brackets = props.product.prices;
  if (!brackets) return [];

  const bracketsWithoutDuplicates = brackets.reduce((acc, bracket) => {
    if (acc.every(a => a.price !== bracket.price)) {
      acc.push(bracket);
    }
    return acc;
  }, []);

  const { length } = bracketsWithoutDuplicates;
  return bracketsWithoutDuplicates.map((b, idx: number) => {
    const next = idx + 1;
    const bracketText = length <= next
      ? bracketNameText(b.bracket)
      : bracketNameText(`${b.bracket}-${bracketsWithoutDuplicates[next].bracket - 1}`);

    let matchingIndex = idx;
    const variantPrices = configuratorState.value.selections.selectedTechniqueVariantPrices;
    if (!variantPrices[matchingIndex]) {
      matchingIndex = variantPrices.length && variantPrices.length - 1;
    }
    const decoPrice = variantPrices[matchingIndex]?.price || 0;
    const decoPriceText = props.hasDecoration ? ` (+${formatPrice(decoPrice, undefined, '', '', 'decimal')})` : '';
    const priceText = bracketPriceText(`${formatPrice(b.price, undefined, '', '', 'decimal')}` + decoPriceText);
    const selectedPriceText = bracketPriceText(`${formatPrice(b.price + decoPrice, undefined, '', '', 'decimal')}`);
    return {
      bracketName: b.bracket,
      bracketText,
      priceText,
      selectedPriceText,
      price: b.price,
      decorationPrice: decoPrice,
    };
  });
});

const activeBracket = computed(() => {
  if (productPriceBrackets.value.length < 1) {
    return undefined;
  }
  const sum = configuratorState.value.sumQuantity;
  return Array.from(productPriceBrackets.value)
              .sort((a, b) => b.bracketName - a.bracketName)
              .find(b => b.bracketName <= sum)
    || productPriceBrackets.value[0];
});

function bracketNameText(name: string) {
  return `${name} ${websiteText('productdetailspage_printpieceabbreviated').value}`;
}

function bracketPriceText(price: number) {
  return `${price} ${getCurrencySymbol()}/${websiteText('productdetailspage_printpieceabbreviated').value}`;
}

function updateToggle() {
  toggleState.value = !toggleState.value;
  localStorage.setItem("configurator:bracket-accordion-open-state", JSON.stringify(toggleState.value));
}
</script>

<template>
  <div class="config-price-brackets" :open="toggleState">
    <div
      tabindex="0"
      class="config-price-brackets__summary"
      @keypress.space.prevent="updateToggle()"
      @click="updateToggle()"
    >
      <p v-if="activeBracket" class="config-price-brackets__title-bracket">
        {{activeBracket.bracketText}}
      </p>
      <div class="config-price-brackets__title-price-container">
        <p
          v-if="activeBracket"
          :class="['config-price-brackets__title-price',
          {'config-price-brackets__title-price--highlighted': inputFocus}]">
          {{activeBracket.selectedPriceText}}
        </p>
        <ConfiguratorChevron
          :open="toggleState"
        ></ConfiguratorChevron>
      </div>
    </div>
    <div :open="toggleState" class="config-price-brackets__content">
      <p class="config-price-brackets__content-title">
        {{ websiteText('decoration__price_bracket').value }}
      </p>
      <div class="config-price-brackets__titles">
        <div class="config-price-brackets__price-table-head-quantity">
          {{ websiteText('common__quantity').value }}
        </div>
        <div v-if="hasDecoration" class="config-price-brackets__price-table-head-parts">
          {{ websiteText('configurator__product_decoration').value }}
        </div>
        <div v-if="!hasDecoration" class="config-price-brackets__price-table-head-parts">
          {{ websiteText('configurator__product_no_decoration').value }}
        </div>
      </div>
      <div
        v-if="(productPriceBrackets.length > 0)"
        class="config-price-brackets__brackets-container"
      >
        <div
          :class="['config-price-brackets__bracket', {
            'config-price-brackets__bracket--active'
            : bracket.bracketName === activeBracket?.bracketName}]"
          v-for="bracket in productPriceBrackets"
          :key="bracket"
        >
          <div class="config-price-brackets__bracket-range">
            {{bracket.bracketText}}
          </div>
          <div class="config-price-brackets__bracket-price">
            {{bracket.priceText}}
          </div>
        </div>
      </div>
      <p class="config-price-brackets__general-info">
        {{ websiteText('configurator__bracket_info').value }}
      </p>
      <p class="config-price-brackets__decoration-info">
        {{ websiteText('configurator__bracket_decoration_info').value }}
      </p>
    </div>
  </div>
</template>

<style>
  .config-price-brackets {
    padding: 13px 20px 13px;
    border: 1px solid var(--color-neutrals-50);
    border-radius: 3px;
  }

  .config-price-brackets__summary {
    display: flex;
    justify-content: space-between;
    align-items: center;
    user-select: none;
    cursor: pointer;
  }

  .config-price-brackets__content {
    padding: 13px 0 7px;
    overflow: hidden;
    display: none;
  }

  .config-price-brackets__content[open="true"] {
    display: block;
  }

  .config-price-brackets__content-title {
    margin: 10px 0 20px;
    font-weight: 700;
    font-size: 16px;
  }

  .config-price-brackets__title-bracket {
    margin: 0;
    font-weight: 700;
  }

  .config-price-brackets__title-price-container {
    display: flex;
    align-items: center;
  }

  .config-price-brackets__title-price {
    margin: 0 12px 0 0;
    color: var(--color-text-medium);
  }

  .config-price-brackets__title-price--highlighted {
    color: var(--color-feedback-information);
  }

  .config-price-brackets__titles {
    border-bottom: 1px solid var(--color-neutrals-20);
    color: var(--color-text-darker);
    line-height: 16px;
    font-size: 12px;
    font-style: italic;
    margin-bottom: 8px;
    padding-bottom: 8px;
    display: flex;
    justify-content: space-between;
  }

  .config-price-brackets__brackets-container {
    display: grid;
    row-gap: 8px;
  }

  .config-price-brackets__bracket {
    display: flex;
    justify-content: space-between;
    line-height: 24px;
    color: var(--color-neutrals-60);
  }

  .config-price-brackets__bracket--active {
    color: var(--color-text-darker);
  }

  .config-price-brackets__general-info,
  .config-price-brackets__decoration-info {
    line-height: 16px;
    font-size: 12px;
    font-style: italic;
    margin: 0 0 10px;
    text-align: center;
  }

  .config-price-brackets__general-info {
    margin-top: 30px;
  }
</style>
